import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Pencil, 
  Trash2,
  User, 
  Building2, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  FileText,
  Upload,
  Download,
  File,
  MessageSquare,
  Send,
  Crown,
  Clock,
  MoreVertical,
  X,
  Car,
  Heart,
  ShoppingCart,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [customer, setCustomer] = useState(null);
  const [cars, setCars] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileType, setFileType] = useState('document');
  const [deleteFileId, setDeleteFileId] = useState(null);
  const [deleteCommentId, setDeleteCommentId] = useState(null);

  const fetchCustomer = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/customers/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomer(data);
      } else {
        toast.error('Kunde nicht gefunden');
        navigate('/admin/customers');
      }
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const fetchCars = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cars`);
      if (res.ok) {
        const data = await res.json();
        setCars(data);
      }
    } catch (error) {
      console.error('Error fetching cars:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contracts?customer_id=${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  useEffect(() => {
    fetchCustomer();
    fetchCars();
    fetchContracts();
  }, [id]);

  const getCarById = (carId) => cars.find(c => c.id === carId);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSendingComment(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/customers/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: newComment })
      });

      if (res.ok) {
        toast.success('Kommentar hinzugefügt');
        setNewComment('');
        fetchCustomer();
      } else {
        toast.error('Fehler beim Hinzufügen');
      }
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    } finally {
      setSendingComment(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!deleteCommentId) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/customers/${id}/comments/${deleteCommentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Kommentar gelöscht');
        fetchCustomer();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteCommentId(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);

      const res = await fetch(`${API_URL}/api/customers/${id}/files`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('Datei hochgeladen');
        fetchCustomer();
      } else {
        toast.error('Fehler beim Hochladen');
      }
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async () => {
    if (!deleteFileId) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/customers/${id}/files/${deleteFileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Datei gelöscht');
        fetchCustomer();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteFileId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'contract': return '📄';
      case 'document': return '📋';
      default: return '📎';
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800 border-green-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200',
      vip: 'bg-amber-100 text-amber-800 border-amber-200',
      prospect: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    const labels = {
      active: 'Aktiv',
      inactive: 'Inaktiv',
      vip: 'VIP',
      prospect: 'Interessent'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${styles[status] || styles.active}`}>
        {status === 'vip' && <Crown size={14} className="inline mr-1" />}
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/customers" className="p-2 hover:bg-[#F9F9F7] rounded transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-heading">
                {customer.first_name} {customer.last_name}
              </h1>
              {getStatusBadge(customer.status)}
            </div>
            <p className="text-sm text-[#666666] mt-1">
              {customer.customer_number}
              {customer.company && ` • ${customer.company}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/admin/contracts?customer=${id}`}>
            <Button className="btn-primary flex items-center gap-2">
              <FileText size={16} />
              Vertrag erstellen
            </Button>
          </Link>
          <Link to={`/admin/customers/${id}/edit`}>
            <Button className="btn-secondary flex items-center gap-2">
              <Pencil size={16} />
              Bearbeiten
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Card */}
          <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
            <h2 className="font-heading text-lg mb-4">Kontaktdaten</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {customer.email && (
                <div className="flex items-start gap-3">
                  <Mail size={18} className="text-[#999999] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#666666]">E-Mail</p>
                    <a href={`mailto:${customer.email}`} className="text-gold hover:underline">
                      {customer.email}
                    </a>
                  </div>
                </div>
              )}
              {customer.phone && (
                <div className="flex items-start gap-3">
                  <Phone size={18} className="text-[#999999] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#666666]">Telefon</p>
                    <a href={`tel:${customer.phone}`} className="hover:text-gold">
                      {customer.phone}
                    </a>
                  </div>
                </div>
              )}
              {customer.mobile && (
                <div className="flex items-start gap-3">
                  <Phone size={18} className="text-[#999999] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#666666]">Mobil</p>
                    <a href={`tel:${customer.mobile}`} className="hover:text-gold">
                      {customer.mobile}
                    </a>
                  </div>
                </div>
              )}
              {(customer.street || customer.city) && (
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#999999] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#666666]">Adresse</p>
                    <p>{customer.street}</p>
                    <p>{customer.zip_code} {customer.city}</p>
                    <p>{customer.country}</p>
                  </div>
                </div>
              )}
              {customer.date_of_birth && (
                <div className="flex items-start gap-3">
                  <Calendar size={18} className="text-[#999999] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#666666]">Geburtsdatum</p>
                    <p>{new Date(customer.date_of_birth).toLocaleDateString('de-DE')}</p>
                  </div>
                </div>
              )}
              {customer.customer_type === 'business' && customer.tax_id && (
                <div className="flex items-start gap-3">
                  <Building2 size={18} className="text-[#999999] mt-0.5" />
                  <div>
                    <p className="text-sm text-[#666666]">Steuernummer</p>
                    <p>{customer.tax_id}</p>
                  </div>
                </div>
              )}
            </div>

            {customer.notes && (
              <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
                <h3 className="text-sm font-medium mb-2">Interne Notizen</h3>
                <p className="text-[#666666] whitespace-pre-wrap">{customer.notes}</p>
              </div>
            )}
          </div>

          {/* Linked Cars */}
          {(customer.interested_cars?.length > 0 || customer.purchased_cars?.length > 0) && (
            <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
              <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
                <Car size={20} />
                Verknüpfte Fahrzeuge
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Interessierte Fahrzeuge */}
                {customer.interested_cars?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-3 flex items-center gap-2 text-rose-600">
                      <Heart size={14} />
                      Interessiert an ({customer.interested_cars.length})
                    </h3>
                    <div className="space-y-2">
                      {customer.interested_cars.map(carId => {
                        const car = getCarById(carId);
                        if (!car) return (
                          <div key={carId} className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
                            Fahrzeug nicht mehr verfügbar
                          </div>
                        );
                        return (
                          <Link
                            key={carId}
                            to={`/admin/cars/${carId}/edit`}
                            className="flex items-center justify-between p-3 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                              {car.images?.[0] && (
                                <img 
                                  src={car.images[0].startsWith('http') ? car.images[0] : `${API_URL}${car.images[0]}`}
                                  alt={`${car.brand} ${car.model}`}
                                  className="w-12 h-8 object-cover rounded"
                                />
                              )}
                              <div>
                                <p className="font-medium text-sm">{car.brand} {car.model}</p>
                                <p className="text-xs text-[#666666]">{car.year} • €{car.price?.toLocaleString()}</p>
                              </div>
                            </div>
                            <ExternalLink size={14} className="text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Gekaufte Fahrzeuge */}
                {customer.purchased_cars?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-3 flex items-center gap-2 text-emerald-600">
                      <ShoppingCart size={14} />
                      Gekauft ({customer.purchased_cars.length})
                    </h3>
                    <div className="space-y-2">
                      {customer.purchased_cars.map(carId => {
                        const car = getCarById(carId);
                        if (!car) return (
                          <div key={carId} className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
                            Fahrzeug nicht mehr verfügbar
                          </div>
                        );
                        return (
                          <Link
                            key={carId}
                            to={`/admin/cars/${carId}/edit`}
                            className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                              {car.images?.[0] && (
                                <img 
                                  src={car.images[0].startsWith('http') ? car.images[0] : `${API_URL}${car.images[0]}`}
                                  alt={`${car.brand} ${car.model}`}
                                  className="w-12 h-8 object-cover rounded"
                                />
                              )}
                              <div>
                                <p className="font-medium text-sm">{car.brand} {car.model}</p>
                                <p className="text-xs text-[#666666]">{car.year} • €{car.price?.toLocaleString()}</p>
                              </div>
                            </div>
                            <ExternalLink size={14} className="text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Customer Contracts */}
          <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg">Kaufverträge</h2>
              <Link to={`/admin/contracts?customer=${id}`}>
                <Button className="btn-secondary flex items-center gap-2">
                  <FileText size={16} />
                  Neuer Vertrag
                </Button>
              </Link>
            </div>

            {contracts.length === 0 ? (
              <div className="text-center py-8 text-[#999999]">
                <FileText size={32} className="mx-auto mb-2 opacity-50" />
                <p>Keine Verträge vorhanden</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contracts.map((contract) => (
                  <div 
                    key={contract.id}
                    className="flex items-center justify-between p-4 bg-[#F9F9F7] rounded-lg hover:bg-[#F0F0ED] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        contract.status === 'signed' ? 'bg-green-100 text-green-600' :
                        contract.status === 'sent' ? 'bg-blue-100 text-blue-600' :
                        'bg-amber-100 text-amber-600'
                      }`}>
                        <FileText size={20} />
                      </div>
                      <div>
                        <p className="font-medium">{contract.car_brand} {contract.car_model}</p>
                        <p className="text-sm text-[#666666]">
                          €{contract.price?.toLocaleString('de-DE')} • {new Date(contract.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        contract.status === 'signed' ? 'bg-green-100 text-green-700' :
                        contract.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                        contract.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        contract.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {contract.status === 'signed' ? 'Unterschrieben' :
                         contract.status === 'sent' ? 'Versendet' :
                         contract.status === 'cancelled' ? 'Storniert' :
                         contract.status === 'draft' ? 'Entwurf' : 'Ausstehend'}
                      </span>
                      <div className="flex items-center gap-1">
                        {/* Send button for draft/pending contracts */}
                        {(contract.status === 'draft' || contract.status === 'pending') && (
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const res = await fetch(`${API_URL}/api/contracts/${contract.id}/send`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  toast.success('Vertrag wurde per E-Mail gesendet');
                                  fetchContracts();
                                } else {
                                  const data = await res.json();
                                  toast.error(data.detail || 'Fehler beim Senden');
                                }
                              } catch (error) {
                                toast.error('Fehler beim Senden');
                              }
                            }}
                            className="p-2 hover:bg-blue-100 text-blue-600 rounded transition-colors"
                            title="Per E-Mail senden"
                          >
                            <Send size={16} />
                          </button>
                        )}
                        <a
                          href={`${API_URL}/api/contracts/${contract.id}/pdf?lang=de`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-white rounded transition-colors"
                          title="PDF herunterladen"
                        >
                          <Download size={16} />
                        </a>
                        <Link
                          to={`/contract/${contract.id}`}
                          target="_blank"
                          className="p-2 hover:bg-white rounded transition-colors"
                          title="Vertrag ansehen"
                        >
                          <ExternalLink size={16} />
                        </Link>
                        {/* Delete button */}
                        <button
                          onClick={async () => {
                            if (window.confirm('Vertrag wirklich löschen?')) {
                              try {
                                const token = localStorage.getItem('token');
                                const res = await fetch(`${API_URL}/api/contracts/${contract.id}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  toast.success('Vertrag gelöscht');
                                  fetchContracts();
                                } else {
                                  toast.error('Fehler beim Löschen');
                                }
                              } catch (error) {
                                toast.error('Fehler beim Löschen');
                              }
                            }
                          }}
                          className="p-2 hover:bg-red-100 text-red-600 rounded transition-colors"
                          title="Vertrag löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Files */}
          <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg">Dateien & Dokumente</h2>
              <div className="flex items-center gap-2">
                <select
                  value={fileType}
                  onChange={(e) => setFileType(e.target.value)}
                  className="input-premium text-sm"
                >
                  <option value="contract">Vertrag</option>
                  <option value="document">Dokument</option>
                  <option value="other">Sonstiges</option>
                </select>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-secondary flex items-center gap-2"
                  disabled={uploadingFile}
                >
                  {uploadingFile ? <div className="spinner-sm" /> : <Upload size={16} />}
                  Hochladen
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                />
              </div>
            </div>

            {customer.files?.length === 0 ? (
              <div className="text-center py-8 text-[#999999]">
                <File size={32} className="mx-auto mb-2 opacity-50" />
                <p>Keine Dateien vorhanden</p>
              </div>
            ) : (
              <div className="space-y-2">
                {customer.files?.map((file) => (
                  <div 
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-[#F9F9F7] rounded-lg group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getFileIcon(file.file_type)}</span>
                      <div>
                        <p className="font-medium">{file.original_name}</p>
                        <p className="text-xs text-[#999999]">
                          {formatFileSize(file.size)} • {formatDate(file.uploaded_at)}
                          <span className="ml-2 px-2 py-0.5 bg-white rounded text-xs">
                            {file.file_type === 'contract' ? 'Vertrag' : file.file_type === 'document' ? 'Dokument' : 'Sonstiges'}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <a
                        href={`${API_URL}${file.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-white rounded transition-colors"
                        title="Herunterladen"
                      >
                        <Download size={16} />
                      </a>
                      <button
                        onClick={() => setDeleteFileId(file.id)}
                        className="p-2 hover:bg-red-50 rounded transition-colors text-red-600"
                        title="Löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
            <h2 className="font-heading text-lg mb-4">Info</h2>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between">
                <span className="text-[#666666]">Erstellt</span>
                <span>{formatDate(customer.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666666]">Aktualisiert</span>
                <span>{formatDate(customer.updated_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666666]">Letzter Kontakt</span>
                <span>{customer.last_contact ? formatDate(customer.last_contact) : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666666]">Bevorzugter Kontakt</span>
                <span className="capitalize">{customer.preferred_contact}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666666]">Typ</span>
                <span className="flex items-center gap-1">
                  {customer.customer_type === 'business' ? (
                    <><Building2 size={14} /> Geschäftlich</>
                  ) : (
                    <><User size={14} /> Privat</>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="bg-white border border-[#E5E5E5] p-6 rounded-lg">
            <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
              <MessageSquare size={18} />
              Kommentare
            </h2>

            {/* Add Comment */}
            <form onSubmit={handleAddComment} className="mb-4">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Neuen Kommentar hinzufügen..."
                className="input-premium w-full h-20 resize-none text-sm"
              />
              <Button 
                type="submit" 
                className="btn-primary w-full mt-2 flex items-center justify-center gap-2"
                disabled={sendingComment || !newComment.trim()}
              >
                {sendingComment ? <div className="spinner-sm" /> : <Send size={14} />}
                Hinzufügen
              </Button>
            </form>

            {/* Comments List */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {customer.comments?.length === 0 ? (
                <p className="text-center text-[#999999] py-4 text-sm">
                  Noch keine Kommentare
                </p>
              ) : (
                customer.comments?.slice().reverse().map((comment) => (
                  <div key={comment.id} className="bg-[#F9F9F7] p-3 rounded-lg group relative">
                    <p className="text-sm whitespace-pre-wrap pr-6">{comment.text}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-[#999999]">
                      <Clock size={12} />
                      {formatDate(comment.created_at)}
                      <span>• {comment.created_by}</span>
                    </div>
                    <button
                      onClick={() => setDeleteCommentId(comment.id)}
                      className="absolute top-2 right-2 p-1 hover:bg-red-100 rounded transition-colors text-red-600 opacity-0 group-hover:opacity-100"
                      title="Löschen"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete File Modal */}
      {deleteFileId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="font-heading text-xl mb-4">Datei löschen?</h3>
            <p className="text-[#666666] mb-6">
              Sind Sie sicher, dass Sie diese Datei löschen möchten?
            </p>
            <div className="flex gap-3 justify-end">
              <Button onClick={() => setDeleteFileId(null)} className="btn-secondary">
                Abbrechen
              </Button>
              <Button onClick={handleDeleteFile} className="bg-red-600 hover:bg-red-700 text-white">
                Löschen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Comment Modal */}
      {deleteCommentId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="font-heading text-xl mb-4">Kommentar löschen?</h3>
            <p className="text-[#666666] mb-6">
              Sind Sie sicher, dass Sie diesen Kommentar löschen möchten?
            </p>
            <div className="flex gap-3 justify-end">
              <Button onClick={() => setDeleteCommentId(null)} className="btn-secondary">
                Abbrechen
              </Button>
              <Button onClick={handleDeleteComment} className="bg-red-600 hover:bg-red-700 text-white">
                Löschen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
